import { useTranslation } from 'react-i18next';
import { faOctagonExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faCog } from '@fortawesome/pro-regular-svg-icons/faCog';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faUserHelmetSafety } from '@fortawesome/pro-regular-svg-icons/faUserHelmetSafety';
import { faVanShuttle } from '@fortawesome/pro-regular-svg-icons/faVanShuttle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Row, theme, Typography } from 'antd';
import dayJs from 'dayjs';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { getFullName } from 'pos-client/components/scheduleBoard/utils/getFullName.ts';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { useSettingsByPath } from 'pos-client/hooks/useSettings.tsx';
import { dayJsWeekdayAsString } from 'pos-client/utils/dateHelpers.ts';
import {
  type ResourceWithAssetModel,
  type ServiceTypesRequestModel,
  ServiceTypesRequestSchema,
} from 'pos-shared/models/schedulingSchema.ts';

export interface ResourceModel {
  data: ResourceWithAssetModel;
  id: string;
  text: string;
}

type ScheduleBoardResourceCellProps = {
  model: ResourceModel;
  editLane: (id: string) => void;
  serviceLocationType: ServiceTypesRequestModel;
};

export const ScheduleBoardResourceCell = ({
  model,
  editLane,
  serviceLocationType,
}: ScheduleBoardResourceCellProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { selectedDate, laneConflicts, serviceTypeFilter } =
    useScheduleBoardContext();
  const selectedDateDayJs = dayJs(selectedDate);
  const selectedDateWeekday = dayJsWeekdayAsString(selectedDateDayJs);
  const { first: defaultSetting } = useSettingsByPath({
    path: 'Features>EnableDefaultLanes',
  });

  const defaultEnabled = defaultSetting?.value === 'true';
  const lanesDefaultDays = model?.data?.defaultDays ?? [];

  const laneIdsWithConflicts = laneConflicts
    ?.filter(
      (conflict) =>
        selectedDateDayJs.isSame(dayJs(conflict.date), 'day') &&
        conflict.conflicts.some((item) => !item.acknowledged)
    )
    .map((conflict) => conflict.laneId);
  const laneHasConflict = laneIdsWithConflicts?.includes(model.id);

  const { hasPermissions } = usePermissions();
  const canEditLane = hasPermissions('edit.lane');
  const isDefaultLane =
    defaultEnabled && lanesDefaultDays.includes(selectedDateWeekday);

  const textColor = (() => {
    if (laneHasConflict && isDefaultLane) {
      return token.gold8;
    } else if (laneHasConflict) {
      return token.red9;
    } else {
      return token.blue9;
    }
  })();

  const assetPrefix =
    serviceTypeFilter === ServiceTypesRequestSchema.enum.Static
      ? `${t('scheduling.lanes.lane')}: `
      : `${t('scheduling.lanes.route')} `;

  const modalIcon =
    serviceLocationType === ServiceTypesRequestSchema.enum.Mobile
      ? faVanShuttle
      : faUserHelmetSafety;
  const modalAncillaryIcon =
    serviceLocationType === ServiceTypesRequestSchema.enum.Mobile
      ? faCog
      : faPlus;

  return (
    <Row
      className={`resource-header-cell ${laneHasConflict && 'header-conflict'} ${isDefaultLane && 'header-default-lane'}`}
      wrap={false}
      style={{
        height: '100%',
      }}
    >
      <Col
        style={{
          fontSize: '12px',
          fontStyle: 'normal',
          lineHeight: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography.Text style={{ display: 'flex' }}>
          <Typography.Text
            strong
            style={{
              color: textColor,
              fontSize: token.fontSizeSM,
              wordBreak: 'normal',
            }}
          >
            {isDefaultLane ? t('scheduling.lanes.default') : assetPrefix}
          </Typography.Text>
          <Typography.Text
            style={{
              fontSize: token.fontSizeSM,
              color: textColor,
              fontWeight: 400,
            }}
          >
            {model.data?.name}
          </Typography.Text>
        </Typography.Text>
        {serviceLocationType === ServiceTypesRequestSchema.enum.Mobile &&
          model.data?.assignedAssets?.map((asset) => (
            <Typography
              key={asset.id}
              style={{
                fontSize: token.fontSizeSM,
                fontWeight: 400,
                color: 'rgba(0,0,0,0.45)',
              }}
            >
              {asset.type === 'Technician' ? getFullName(asset) : asset.name}
            </Typography>
          ))}
      </Col>
      <Col style={{ display: 'flex', alignItems: 'center' }}>
        {!isDefaultLane && laneHasConflict ? (
          <>
            <Typography.Text
              strong
              style={{
                textTransform: 'uppercase',
                marginRight: token.marginXS,
                color: token['red-9'],
              }}
            >
              {t('scheduling.conflictLanes.conflict')}
            </Typography.Text>
            <FontAwesomeIcon
              color={token['red-9']}
              icon={faOctagonExclamation}
            />
            {canEditLane && (
              <Button
                icon={
                  <>
                    <FontAwesomeIcon
                      style={{
                        color: token.red9,
                        marginRight: token.marginXXS,
                      }}
                      icon={modalIcon}
                    />
                    <FontAwesomeIcon
                      style={{ color: token.red9 }}
                      icon={modalAncillaryIcon}
                    />
                  </>
                }
                onClick={() => editLane(model.data.id)}
                type="text"
              />
            )}
          </>
        ) : (
          canEditLane && (
            <Button
              icon={
                <>
                  <FontAwesomeIcon
                    style={{ color: textColor, marginRight: token.marginXXS }}
                    icon={modalIcon}
                  />
                  <FontAwesomeIcon
                    style={{ color: textColor }}
                    icon={modalAncillaryIcon}
                  />
                </>
              }
              onClick={() => editLane(model.data.id)}
              type="text"
            />
          )
        )}
      </Col>
    </Row>
  );
};
